import { flatten, mergeDeepWith, reduce } from 'ramda';
import { get } from 'lodash';
import getOr from 'lodash/fp/getOr';

import { INDETERMINATE } from '../../../../common/constants';
import { defPermissions } from '../../../../auth/utils/defaultPermissions';

/**
 * Get permissions in a permission row
 * @param rowPermissionsData - permissions row data (of type ./permissionsData/permissionRowPropType)
 * @returns {Object[]} - array of permissions in a row (array of type ./permissionsData/permissionPropType)
 */
export const getRowDependencies = rowPermissionsData => Object.values(rowPermissionsData.permissions);

/**
 * Get permissions in a permission section
 * @param sectionPermissionsData - permission section data (of type ./permissionsData/permissionSectionPropType)
 * @returns {Object[]} - array of permissions in a section (array of type ./permissionsData/permissionPropType)
 */
export const getSectionDependencies = sectionPermissionsData => flatten(
  sectionPermissionsData.rows.map(getRowDependencies)
);

/**
 * Check if any permission has changed in a row/section
 * @param dependencies - permissions in a row/section (array of type ./permissionsData/permissionPropType)
 * @param prevFormValues - prevProps.formValues
 * @param nextFormValues - nextProps.formValues
 * @returns {boolean} true if any permission has changed
 */
export const isDependenciesChanged = (dependencies, prevFormValues, nextFormValues) => {
  if (!dependencies || !dependencies.length) {
    return false;
  }

  return dependencies.some(
    dependency => get(prevFormValues, dependency.fieldName) !== get(nextFormValues, dependency.fieldName)
  );
};

/**
 * Gets a value of a row/section summary checkbox based on dependent checkbox values in the row/section
 * @param dependencies - permissions in a row/section (array of type ./permissionsData/permissionPropType)
 * @param formValues - form values
 * @returns { boolean | INDETERMINATE}
 */
export const getSummaryCheckboxValue = (dependencies, formValues) => {
  if (!dependencies || !dependencies.length) {
    return false;
  }

  return reduce((result, dependency) => {
    if (result === INDETERMINATE) {
      return result;
    }

    const dependencyValue = !!get(formValues, dependency.fieldName);

    if (result === null) {
      return dependencyValue;
    }

    if (result !== dependencyValue) {
      return INDETERMINATE;
    }

    return result;
  }, null, dependencies);
};

/**
 * On permission value change,
 * check 'requires' and 'requiredFor' dependencies of the permission and update them if necessary
 * @param permissionData - permission data (of type ./permissionsData/permissionPropType)
 * @param {boolean} newPermissionValue - new value of the permission checkbox
 * @param {string} formName - form name
 * @param {function} changeFormValue - function to change form value
 */
export const updateDependentPermissions = ({ permissionData, newPermissionValue, formName, changeFormValue }) => {
  const requires = permissionData.requires;
  if (newPermissionValue && requires && requires.length) {
    requires.forEach((permissionToTurnOn) => {
      changeFormValue(formName, permissionToTurnOn, true);
    });
  }

  const requiredFor = permissionData.requiredFor;
  if (!newPermissionValue && requiredFor && requiredFor.length) {
    requiredFor.forEach((permissionToTurnOff) => {
      changeFormValue(formName, permissionToTurnOff, false);
    });
  }
};

/**
 * Staff manager cannot change other user's permission if they don't have that permission themselves
 * @param {string} permissionFieldName - permission to check, e.g. 'permissions.barber.modify'
 * @param {Object.<{ permissions: Object }>} permissionsMask - staff managers permissions
 * @return true if staff manager's permissions allows to change this permission for other users
 */
export const canUpdatePermission = (permissionFieldName, permissionsMask) =>
  getOr(false, permissionFieldName, permissionsMask);

const defaultNewStaffPermissions = {
  ...defPermissions,
  staffManagement: {
    view: true,
    modify: true,
  },
  engage: {
    view: false,
  },
  cashRegister: {
    view: true,
    modify: false,
  },
  customCharge: {
    view: false,
  },
  kiosk: {
    view: false,
  },
};

export const getNewStaffPermissions = currentUserPermissions => mergeDeepWith(
  (defPermission, currentUserPermission) => !!(defPermission && currentUserPermission),
  defaultNewStaffPermissions,
  currentUserPermissions,
);
